import React from 'react';
import { Card, TrumpSuit, Direction } from '../../state/ArenaTypes';
import { Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { AuctionTurnTimerBadge } from '../Auction/AuctionTurnTimerBadge';
import { CardView } from '../Card/CardView';
import { arenaAudio } from '../../services/ArenaAudioService';

export interface KittyDiscardHudProps {
  selectedDiscardCount: number;
  discardedCards?: Card[];
  onUndiscardCard?: (card: Card) => void;
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
  isSubmittingDiscards: boolean;
  onConfirmDiscards?: () => void;
  winningBid?: number;
  direction?: Direction;
  trumpSuit?: TrumpSuit | null;
}


export const KittyDiscardHud: React.FC<KittyDiscardHudProps> = ({
  selectedDiscardCount,
  discardedCards = [],
  onUndiscardCard,
  turnTimerSeconds,
  turnTimerMax = 40,
  isSubmittingDiscards,
  onConfirmDiscards,
}) => {
  // Retain discarded cards and count strictly during the submission/exit animation when parent clears selections
  const lastCardsRef = React.useRef<Card[]>(discardedCards);
  if (isSubmittingDiscards && discardedCards.length > 0) {
    lastCardsRef.current = discardedCards;
  }
  const effectiveDiscardedCards = isSubmittingDiscards && discardedCards.length === 0
    ? lastCardsRef.current
    : discardedCards;
  const effectiveCount = isSubmittingDiscards && selectedDiscardCount === 0
    ? effectiveDiscardedCards.length
    : selectedDiscardCount;
  const isReady = effectiveCount === 6;

  const maxTimer = turnTimerMax && turnTimerMax > 0 ? turnTimerMax : 40;
  const redThreshold = Math.max(5, Math.round(maxTimer * 0.25));
  const isCritical = turnTimerSeconds !== undefined && turnTimerSeconds !== null && turnTimerSeconds <= redThreshold;

  const handleConfirmClick = React.useCallback(() => {
    if (!isReady || isSubmittingDiscards) return;
    try {
      arenaAudio.playKittyTuck();
    } catch {}
    onConfirmDiscards?.();
  }, [isReady, isSubmittingDiscards, onConfirmDiscards]);

  return (
    <motion.div
      key="kitty-discard-hud"
      initial={{ opacity: 0, scale: 0.95, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{
        opacity: 0,
        scale: 0.95,
        y: 12,
        transition: { duration: 0.2, ease: [0.32, 0, 0.67, 0] }
      }}
      transition={{ type: 'spring', damping: 24, stiffness: 300, mass: 0.8 }}
      className="w-[calc(100%-1rem)] max-w-[500px] sm:max-w-[590px] md:max-w-[640px] pointer-events-auto select-none rounded-[28px] bg-[#0a1120] transition-[border-color,box-shadow] duration-300 transform-gpu [backface-visibility:hidden] [webkit-backface-visibility:hidden] relative acs-font-scale-110"
      style={{
        border: isCritical ? '1.5px solid #f43f5e' : '1.5px solid #d48b28',
        boxShadow: isCritical
          ? '0 0 10px 1px rgba(244, 63, 94, 0.5), 0 16px 45px rgba(0, 0, 0, 0.85)'
          : '0 0 10px 1px rgba(229, 142, 25, 0.35), 0 16px 45px rgba(0, 0, 0, 0.85)',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'transform, opacity',
      }}
    >
      {/* CIRCULAR TIMER BADGE SITTING ON TOP OF THE BORDER */}
      {turnTimerSeconds !== undefined && turnTimerSeconds !== null && (
        <AuctionTurnTimerBadge
          turnTimerSeconds={turnTimerSeconds}
          turnTimerMax={turnTimerMax}
          className="absolute -top-7 left-1/2 -translate-x-1/2 z-30"
        />
      )}

      {/* INNER GRADIENT CONTAINER */}
      <div className="w-full h-full rounded-[27px] px-3 sm:px-5 pt-7 sm:pt-8 pb-2.5 sm:pb-3 relative z-10 flex flex-col space-y-2 sm:space-y-2.5 bg-gradient-to-b from-[#0e1728] via-[#09101d] to-[#050912]">
        {/* Subtle Watermarks (Spade Upper Left + Crown Upper Right matching DeclareTrumpHud) */}
        <div className="absolute inset-0 rounded-[27px] overflow-hidden pointer-events-none select-none">
          {/* Spade Silhouette */}
          <svg
            className="absolute top-0.5 left-1 w-20 h-20 sm:w-24 sm:h-24 rotate-[24deg] text-slate-300/[0.08] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Two-Tiered Crown Silhouette */}
          <svg
            className="absolute top-0.5 right-1 w-22 h-20 sm:w-25 sm:h-22 -rotate-[26deg] select-none pointer-events-none"
            viewBox="0 0 100 100"
          >
            <defs>
              <linearGradient id="crownWatermarkGradKittyDiscard" x1="20%" y1="0%" x2="80%" y2="100%">
                <stop offset="0%" stopColor="#fde68a" stopOpacity="0.14" />
                <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#b45309" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <path
              fill="url(#crownWatermarkGradKittyDiscard)"
              d="M 14 83 Q 50 86 86 83 L 86 91 Q 50 94 14 91 Z"
            />
            <path
              fill="url(#crownWatermarkGradKittyDiscard)"
              d="M 16 76 L 84 76 L 90 36 C 92 30 82 28 78 34 C 68 44 64 48 57 46 L 57 28 L 66 18 L 50 5 L 34 18 L 43 28 L 43 46 C 36 48 32 44 22 34 C 18 28 8 30 10 36 Z"
            />
          </svg>
        </div>

        {/* SECTION 1: HEADER & TITLE */}
        <div className="flex flex-col items-center justify-center text-center relative z-10 w-full pt-0.5">
          <div className="flex items-center justify-center gap-3 w-full px-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-amber-400/70" />
            <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight shrink-0">
              <span className="text-[#f5c544]">Kitty</span> <span className="text-white">Discard</span>
            </h2>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/40 to-amber-400/70" />
          </div>

          <div className="flex items-center justify-center mt-1">
            <span
              className={`text-[10.5px] sm:text-[11.5px] font-black px-2.5 py-0.5 rounded-full border transition-all ${
                isReady
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]'
                  : 'bg-[#081220] text-amber-400 border-amber-500/30'
              }`}
            >
              {effectiveCount} / 6 Discards
            </span>
          </div>
        </div>

        {/* SEPARATOR LINE */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent my-0.5 relative z-10" />

        {/* SECTION 2: 6 DISCARD SLOTS TRAY */}
        <div className="p-1.5 sm:p-2.5 rounded-xl bg-[#091220]/90 border border-white/15 shadow-inner relative z-10 flex items-center justify-center">
          <LayoutGroup id="kitty-discard-slots">
            <div className="grid grid-cols-6 gap-1.5 sm:gap-4 justify-items-center items-center w-full">
              {Array.from({ length: 6 }).map((_, slotIdx) => {
                const card = effectiveDiscardedCards[slotIdx];
                const isFilled = Boolean(card) || slotIdx < effectiveCount;
                return (
                  <div
                    key={`discard_slot_${slotIdx}`}
                    className={`w-12 h-17 sm:w-16 sm:h-23 rounded-lg sm:rounded-xl border-2 flex flex-col items-center justify-center relative transition-colors duration-150 overflow-hidden ${
                      isFilled
                        ? 'border-amber-400/60 bg-amber-500/10 shadow-[0_0_12px_rgba(245,158,11,0.25)]'
                        : 'border-dashed border-white/20 bg-black/30'
                    }`}
                  >
                    {/* Underlying slot number, visible when empty */}
                    <div className="flex flex-col items-center justify-center text-white/30 select-none pointer-events-none">
                      <span className="font-mono text-xs sm:text-base font-black">{slotIdx + 1}</span>
                    </div>

                    <AnimatePresence>
                      {card && (
                        <motion.div
                          key={card.id}
                          layoutId={`discard_card_${card.id}`}
                          layout
                          initial={{ scale: 0.85, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.82, opacity: 0 }}
                          transition={{
                            layout: { type: 'spring', stiffness: 420, damping: 32, mass: 0.5 },
                            scale: { duration: 0.16, ease: [0.16, 1, 0.3, 1] },
                            opacity: { duration: 0.12, ease: 'easeOut' }
                          }}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.94 }}
                          onClick={() => onUndiscardCard?.(card)}
                          className="absolute inset-0 w-full h-full flex items-center justify-center cursor-pointer hover:brightness-110"
                          title={`Click to remove ${card.displayValue || card.id} from discards`}
                        >
                          <div className="scale-[0.62] sm:scale-[0.76] origin-center shrink-0 pointer-events-none">
                            <CardView
                              card={card}
                              isPlayable={true}
                              isSelected={false}
                              size="sm"
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </LayoutGroup>
        </div>

        {/* SECTION 3: CONFIRM BUTTON - ALWAYS PRESENT */}
        <motion.button
          type="button"
          whileHover={isReady && !isSubmittingDiscards ? { scale: 1.015 } : undefined}
          whileTap={isReady && !isSubmittingDiscards ? { scale: 0.97 } : undefined}
          disabled={!isReady || isSubmittingDiscards}
          onClick={handleConfirmClick}
          className={`w-full h-10.5 sm:h-11 px-3 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border relative z-10 ${
            isReady && !isSubmittingDiscards
              ? 'bg-gradient-to-r from-[#ffd152] via-[#ffb923] to-[#f59e0b] hover:from-[#ffe072] hover:to-[#ffb923] text-slate-950 border-amber-200/70 shadow-[0_4px_16px_rgba(245,158,11,0.45)] cursor-pointer active:scale-95'
              : 'bg-white/[0.04] text-white/25 border-white/10 cursor-not-allowed shadow-none'
          }`}
        >
          {isSubmittingDiscards ? (
            <>
              <Loader2 size={16} className="animate-spin text-slate-950 shrink-0" />
              <span>Confirming Discards...</span>
            </>
          ) : isReady ? (
            <>
              <Check size={16} className="stroke-[3] text-slate-950 shrink-0" />
              <span>Confirm Discards & Play</span>
            </>
          ) : (
            <span>
              {effectiveCount === 0
                ? 'Select 6 Cards to Discard (0/6)'
                : `Select ${6 - effectiveCount} More Card${6 - effectiveCount === 1 ? '' : 's'} (${effectiveCount}/6)`}
            </span>
          )}
        </motion.button>

        {/* DECORATIVE FOOTER DIVIDER: ─── ♠ ─── */}
        <div className="flex items-center justify-center gap-3 w-full pt-0.5 pb-0.5 select-none relative z-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-amber-400/50" />
          <span className="text-amber-400 text-base sm:text-lg select-none leading-none drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">♠</span>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/30 to-amber-400/50" />
        </div>
      </div>
    </motion.div>
  );
};
